import React, { useState, useEffect } from 'react';
import { getPendingTestAccountRequests, approveTestAccountRequest, rejectTestAccountRequest } from '../../../services/testAccountService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import toast from 'react-hot-toast';

const TestAccountApprovalQueue = () => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setIsLoading(true);
    try {
      let result = await getPendingTestAccountRequests();
      if (result?.success) {
        setRequests(result?.data || []);
      } else {
        toast?.error('Failed to load test account requests');
      }
    } catch (error) {
      console.error('Load requests error:', error);
      toast?.error('Error loading requests');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (request) => {
    setSelectedRequest(request);
    setActionType('approve');
    setShowReasonModal(true);
  };

  const handleReject = async (request) => {
    setSelectedRequest(request);
    setActionType('reject');
    setShowReasonModal(true);
  };

  const confirmAction = async () => {
    if (!selectedRequest) return;

    setProcessingId(selectedRequest?.id);
    try {
      let result;
      if (actionType === 'approve') {
        result = await approveTestAccountRequest(selectedRequest?.id, reason || 'Approved by admin');
        if (result?.success) {
          toast?.success(`Test account approved for ${selectedRequest?.email}`);
        }
      } else {
        result = await rejectTestAccountRequest(selectedRequest?.id, reason || 'Rejected by admin');
        if (result?.success) {
          toast?.success(`Test account rejected for ${selectedRequest?.email}`);
        }
      }

      if (!result?.success) {
        toast?.error(result?.error || 'Action failed');
      } else {
        // Reload requests
        await loadRequests();
      }
    } catch (error) {
      console.error('Action error:', error);
      toast?.error('Error processing request');
    } finally {
      setProcessingId(null);
      setShowReasonModal(false);
      setSelectedRequest(null);
      setReason('');
      setActionType(null);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="UserCheck" size={24} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Test Account Approval Queue</h3>
            <p className="text-sm text-muted-foreground">
              Review and approve test account requests with special codes
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {requests?.length} pending {requests?.length === 1 ? 'request' : 'requests'}
          </span>
          <button
            onClick={loadRequests}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            title="Refresh"
          >
            <Icon name="RefreshCw" size={20} className="text-muted-foreground" />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading requests...</div>
      ) : requests?.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="CheckCircle" size={48} className="mx-auto mb-2 opacity-50" />
          <p>No pending test account requests</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests?.map((request) => (
            <div
              key={request?.id}
              className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-base font-semibold text-foreground">
                      {request?.full_name || 'No name provided'}
                    </h4>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        request?.role === 'expert' ?'bg-secondary/10 text-secondary' :'bg-primary/10 text-primary'
                      }`}
                    >
                      {request?.role?.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{request?.email}</p>
                </div>
                <span className="text-xs text-muted-foreground">
                  {new Date(request?.test_account_requested_at)?.toLocaleString()}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Test Account Code</p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                      {request?.test_account_code || 'N/A'}
                    </code>
                    {request?.code_is_valid ? (
                      <span className="inline-flex items-center gap-1 text-xs text-success">
                        <Icon name="CheckCircle" size={14} />
                        Valid
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-warning">
                        <Icon name="AlertTriangle" size={14} />
                        Invalid Code
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Signup Date</p>
                  <p className="text-sm text-foreground">
                    {new Date(request?.created_at)?.toLocaleDateString()}
                  </p>
                </div>
              </div>

              {!request?.code_is_valid && (
                <div className="mb-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-warning mb-1">Invalid Code Warning</p>
                      <p className="text-xs text-warning/80">
                        The entered code does not match any expected test codes in the system. Review
                        carefully before approving.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button
                  onClick={() => handleApprove(request)}
                  disabled={processingId === request?.id}
                  className="flex-1 bg-success text-white hover:bg-success/90"
                >
                  <Icon name="Check" size={16} className="mr-2" />
                  {processingId === request?.id ? 'Processing...' : 'Approve'}
                </Button>
                <Button
                  onClick={() => handleReject(request)}
                  disabled={processingId === request?.id}
                  className="flex-1 bg-error text-white hover:bg-error/90"
                >
                  <Icon name="X" size={16} className="mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reason Modal */}
      {showReasonModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`p-2 rounded-lg ${
                  actionType === 'approve' ? 'bg-success/10' : 'bg-error/10'
                }`}
              >
                <Icon
                  name={actionType === 'approve' ? 'Check' : 'X'}
                  size={24}
                  className={actionType === 'approve' ? 'text-success' : 'text-error'}
                />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  {actionType === 'approve' ? 'Approve' : 'Reject'} Test Account Request
                </h3>
                <p className="text-sm text-muted-foreground">{selectedRequest?.email}</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                Reason (Optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e?.target?.value)}
                placeholder="Enter reason for this action..."
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                rows={3}
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={confirmAction}
                className={`flex-1 text-white ${
                  actionType === 'approve' ?'bg-success hover:bg-success/90' :'bg-error hover:bg-error/90'
                }`}
              >
                Confirm {actionType === 'approve' ? 'Approval' : 'Rejection'}
              </Button>
              <Button
                onClick={() => {
                  setShowReasonModal(false);
                  setSelectedRequest(null);
                  setReason('');
                  setActionType(null);
                }}
                className="flex-1 bg-muted text-foreground hover:bg-muted/80"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestAccountApprovalQueue;
